import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Heart, 
  Clock, 
  Users, 
  ArrowLeft, 
  Plus, 
  ShoppingCart,
  Loader2,
  ChefHat,
  Calendar,
  Sparkles,
  Check
} from 'lucide-react';

const RecipeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [checkedIngredients, setCheckedIngredients] = useState(new Set());
  const [excludedIngredients, setExcludedIngredients] = useState(new Set());
  const [addingToList, setAddingToList] = useState(false);
  const [addingToMealPlan, setAddingToMealPlan] = useState(false);

  useEffect(() => {
    const fetchRecipe = async () => {
      try {
        const [recipeRes, favoritesRes] = await Promise.all([
          api.get(`/recipes/${id}`),
          api.get('/favorites')
        ]);

        setRecipe(recipeRes.data);
        
        const favIds = new Set(favoritesRes.data.map(f => f.recipe_id));
        setIsFavorite(favIds.has(parseInt(id)));
      } catch (error) {
        console.error('Failed to fetch recipe:', error);
        toast.error('Failed to load recipe');
      } finally {
        setLoading(false);
      }
    };

    fetchRecipe();
  }, [id, api]);

  const toggleFavorite = async () => {
    try {
      if (isFavorite) {
        await api.delete(`/favorites/${id}`);
        setIsFavorite(false);
        toast.success('Removed from favorites');
      } else {
        await api.post('/favorites', {
          recipe_id: parseInt(id),
          recipe_title: recipe.title,
          recipe_image: recipe.image,
          ready_in_minutes: recipe.readyInMinutes,
          servings: recipe.servings
        });
        setIsFavorite(true);
        toast.success('Added to favorites');
      }
    } catch (error) {
      toast.error('Failed to update favorites');
    }
  };

  const toggleIngredient = (index) => {
    setCheckedIngredients(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const toggleExcludeIngredient = (index, e) => {
    e.stopPropagation();
    setExcludedIngredients(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const addToMealPlan = async () => {
    setAddingToMealPlan(true);
    try {
      // Add to today's meal plan
      const today = new Date().toISOString().split('T')[0];
      await api.post('/meal-plan', {
        date: today,
        recipe_id: parseInt(id),
        recipe_title: recipe.title,
        recipe_image: recipe.image,
        ready_in_minutes: recipe.readyInMinutes,
        servings: recipe.servings
      });
      toast.success('Added to your meal plan!', {
        description: 'View it in Meal Planner',
        action: {
          label: 'View',
          onClick: () => navigate('/meal-planner')
        }
      });
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Upgrade to Pro for unlimited meal planning');
        navigate('/subscription');
      } else {
        toast.error('Failed to add to meal plan');
      }
    } finally {
      setAddingToMealPlan(false);
    }
  };

  const addToGroceryList = async () => {
    if (!recipe?.extendedIngredients?.length) {
      toast.error('No ingredients to add');
      return;
    }

    setAddingToList(true);
    try {
      // Filter out excluded ingredients (ones user already has)
      const items = recipe.extendedIngredients
        .filter((_, index) => !excludedIngredients.has(index))
        .map(ing => ({
          name: ing.name || ing.original,
          amount: ing.amount || 0,
          unit: ing.unit || '',
          aisle: ing.aisle || 'Other',
          checked: false
        }));

      if (items.length === 0) {
        toast.info('All ingredients marked as "already have" - nothing to add!');
        return;
      }

      await api.post('/grocery-lists', {
        name: `${recipe.title} Ingredients`,
        items
      });

      const excludedCount = excludedIngredients.size;
      toast.success(
        excludedCount > 0 
          ? `Grocery list created! (${excludedCount} item${excludedCount > 1 ? 's' : ''} skipped)`
          : 'Grocery list created!'
      );
      navigate('/grocery-lists');
    } catch (error) {
      if (error.response?.data?.detail?.includes('limited')) {
        toast.error('Upgrade to Pro for unlimited grocery lists');
        navigate('/subscription');
      } else {
        toast.error('Failed to create grocery list');
      }
    } finally {
      setAddingToList(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!recipe) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <ChefHat className="h-16 w-16 text-muted-foreground/30 mb-4" />
        <h2 className="text-xl font-medium mb-2">Recipe not found</h2>
        <Button onClick={() => navigate('/recipes')} variant="outline">
          Back to Recipes
        </Button>
      </div>
    );
  }

  const ingredients = recipe.extendedIngredients || [];
  const instructions = recipe.analyzedInstructions?.[0]?.steps || [];
  const nutrition = recipe.nutrition?.nutrients || [];

  return (
    <div className="min-h-screen py-8" data-testid="recipe-detail-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)} 
          className="mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        {/* Hero Section */}
        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          <div className="aspect-[4/3] rounded-2xl overflow-hidden">
            <img 
              src={recipe.image} 
              alt={recipe.title}
              className="w-full h-full object-cover"
            />
          </div>
          
          <div className="flex flex-col justify-center">
            <h1 className="text-3xl md:text-4xl font-semibold font-['Playfair_Display'] mb-4">
              {recipe.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 mb-6">
              {recipe.readyInMinutes && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Clock className="h-5 w-5" />
                  <span>{recipe.readyInMinutes} minutes</span>
                </div>
              )}
              {recipe.servings && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Users className="h-5 w-5" />
                  <span>{recipe.servings} servings</span>
                </div>
              )}
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-6">
              {recipe.vegetarian && <Badge variant="secondary">Vegetarian</Badge>}
              {recipe.vegan && <Badge variant="secondary">Vegan</Badge>}
              {recipe.glutenFree && <Badge variant="secondary">Gluten Free</Badge>}
              {recipe.dairyFree && <Badge variant="secondary">Dairy Free</Badge>}
              {recipe.cuisines?.map(c => (
                <Badge key={c} variant="outline" className="capitalize">{c}</Badge>
              ))}
            </div>

            {/* Actions */}
            <div className="space-y-4">
              <div className="flex flex-wrap gap-3">
                <Button 
                  onClick={addToMealPlan}
                  disabled={addingToMealPlan}
                  className="bg-primary"
                  data-testid="add-to-meal-plan-btn"
                >
                  {addingToMealPlan ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Calendar className="h-4 w-4 mr-2" />
                  )}
                  Add to Meal Plan
                </Button>
                <Button 
                  variant="outline"
                  onClick={addToGroceryList}
                  disabled={addingToList}
                  data-testid="add-to-list-btn"
                >
                  {addingToList ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <ShoppingCart className="h-4 w-4 mr-2" />
                  )}
                  Add Ingredients to Grocery List
                </Button>
                <Button 
                  variant={isFavorite ? "secondary" : "ghost"} 
                  onClick={toggleFavorite}
                  data-testid="favorite-btn"
                >
                  <Heart className={`h-4 w-4 mr-2 ${isFavorite ? 'fill-destructive text-destructive' : ''}`} />
                  {isFavorite ? 'Saved' : 'Save'}
                </Button>
              </div>
              
              {/* Helper text for workflow */}
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                Plan meals first — your grocery list updates automatically.
              </p>
            </div>
          </div>
        </div>

        {/* Summary */}
        {recipe.summary && (
          <Card className="border-border/50 mb-8">
            <CardContent className="pt-6">
              <div 
                className="prose prose-sm max-w-none text-muted-foreground"
                dangerouslySetInnerHTML={{ __html: recipe.summary }}
              />
            </CardContent>
          </Card>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Ingredients */}
          <Card className="border-border/50 lg:col-span-1">
            <CardHeader className="pb-2">
              <CardTitle className="font-['Playfair_Display'] flex items-center justify-between">
                Ingredients
                <Badge variant="outline">{ingredients.length - excludedIngredients.size} of {ingredients.length}</Badge>
              </CardTitle>
              <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-1">
                <Check className="h-3 w-3 text-primary" />
                Automatically added to your grocery list
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {ingredients.map((ing, index) => {
                  const isExcluded = excludedIngredients.has(index);
                  return (
                    <div 
                      key={index}
                      className={`flex items-start gap-3 p-2.5 rounded-lg transition-colors ${
                        isExcluded 
                          ? 'bg-muted/30 opacity-60' 
                          : 'hover:bg-muted/50 cursor-pointer'
                      }`}
                      onClick={() => !isExcluded && toggleIngredient(index)}
                      data-testid={`ingredient-${index}`}
                    >
                      <Checkbox 
                        checked={checkedIngredients.has(index)}
                        onCheckedChange={() => toggleIngredient(index)}
                        disabled={isExcluded}
                        className="mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        <span className={`text-sm block ${
                          checkedIngredients.has(index) || isExcluded 
                            ? 'line-through text-muted-foreground' 
                            : ''
                        }`}>
                          {ing.original || `${ing.amount} ${ing.unit} ${ing.name}`}
                        </span>
                        {isExcluded && (
                          <span className="text-xs text-muted-foreground">Already have this</span>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`h-7 px-2 text-xs shrink-0 ${
                          isExcluded 
                            ? 'text-primary hover:text-primary' 
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                        onClick={(e) => toggleExcludeIngredient(index, e)}
                        data-testid={`exclude-${index}`}
                      >
                        {isExcluded ? 'Add back' : 'Have it'}
                      </Button>
                    </div>
                  );
                })}
              </div>
              
              {/* Excluded count summary */}
              {excludedIngredients.size > 0 && (
                <div className="mt-4 pt-3 border-t border-border">
                  <p className="text-xs text-muted-foreground text-center">
                    {excludedIngredients.size} item{excludedIngredients.size > 1 ? 's' : ''} you already have won&apos;t be added to your list
                  </p>
                </div>
              )}
              
              {/* Microcopy */}
              <p className="text-xs text-muted-foreground mt-4 text-center">
                Ingredients only — cooking steps stay here.
              </p>
            </CardContent>
          </Card>

          {/* Instructions */}
          <Card className="border-border/50 lg:col-span-2">
            <CardHeader>
              <CardTitle className="font-['Playfair_Display']">Instructions</CardTitle>
            </CardHeader>
            <CardContent>
              {instructions.length > 0 ? (
                <ol className="space-y-6">
                  {instructions.map((step, index) => (
                    <li key={index} className="flex gap-4" data-testid={`step-${index}`}>
                      <span className="flex-shrink-0 h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-medium text-sm">
                        {step.number || index + 1}
                      </span>
                      <p className="text-sm leading-relaxed pt-1">{step.step}</p>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="text-muted-foreground text-center py-8">
                  No detailed instructions available.
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Nutrition */}
        {nutrition.length > 0 && (
          <Card className="border-border/50 mt-8">
            <CardHeader>
              <CardTitle className="font-['Playfair_Display']">Nutrition per Serving</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {nutrition.slice(0, 8).map((n, index) => (
                  <div key={index} className="text-center">
                    <p className="text-2xl font-bold text-primary">
                      {Math.round(n.amount)}
                      <span className="text-sm font-normal text-muted-foreground ml-1">
                        {n.unit}
                      </span>
                    </p>
                    <p className="text-sm text-muted-foreground">{n.name}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default RecipeDetail;
